// src/App.tsx
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import api from './services/api';
import MainLayout from './components/layout/MainLayout';
import { LoadingProvider } from './contexts/LoadingContext';

// Auth
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Dashboard
import RoleBasedDashboard from './components/dashboard/RoleBasedDashboard';
import DashboardRektor from './pages/dashboard/DashboardRektor';
import DashboardWarek from './pages/dashboard/DashboardWarek';
import DashboardDekan from './pages/dashboard/DashboardDekan';
import DashboardKaprodi from './pages/dashboard/DashboardKaprodi';
import DashboardLPM from './pages/dashboard/DashboardLPM';
import DashboardAdmin from './pages/dashboard/DashboardAdmin';

// Laporan
import LaporanList from './pages/laporan/LaporanList';
import Arsip from './pages/laporan/Arsip';
import LaporanCreate from './pages/laporan/LaporanCreate';
import LaporanDetail from './pages/laporan/LaporanDetail';
import LaporanEdit from './pages/laporan/LaporanEdit';

// Validasi
import ValidasiLevel1 from './pages/validasi/ValidasiLevel1';
import ValidasiLevel2 from './pages/validasi/ValidasiLevel2';

// RAPIM
import RapimCalendar from './pages/rapim/RapimCalendar';
import RapimCreate from './pages/rapim/RapimCreate';
import RapimDetail from './pages/rapim/RapimDetail';
import RapimKeputusan from './pages/rapim/RapimKeputusan';
import RapimPresentasi from './pages/rapim/RapimPresentasi';

// Monitoring
import MonitoringDashboard from './pages/monitoring/MonitoringDashboard';
import TindakLanjutDetail from './pages/monitoring/TindakLanjutDetail';

// Reports
import ReportBuilder from './pages/reports/ReportBuilder';

// Admin
import UserManagement from './pages/admin/UserManagement';
import UnitManagement from './pages/admin/UnitManagement';
import MasterData from './pages/admin/MasterData';
import RoleScopes from './pages/admin/RoleScopes';
import SystemSettings from './pages/admin/SystemSettings';
import AuditLogs from './pages/admin/AuditLogs';

// Profile
import Profile from './pages/profile/Profile';
import ChangePassword from './pages/profile/ChangePassword';
import UserSettings from './pages/profile/UserSettings';
import NotificationsPage from './pages/profile/Notifications';

// ============ ROUTE GUARDS ============

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

const RoleRoute: React.FC<{ children: React.ReactNode; allowedRoles: string[] }> = ({
  children,
  allowedRoles,
}) => {
  const { user } = useAuthStore();
  const storedRole = localStorage.getItem('user_role') || '';
  const roleName = (user?.role?.name || storedRole || '').toLowerCase();

  if (!allowedRoles.map((r) => r.toLowerCase()).includes(roleName)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const GuestRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

const MONITORING_ALLOWED_ROLES = [
  'admin',
  'rektor',
  'dekan',
  'kalem',
  'lpm',
  'senat',
  'warek',
  'warek1',
  'warek2',
  'warek3',
  'warek4',
  'warek_1',
  'warek_2',
  'warek_3',
  'warek_4',
  'wakil_rektor_1',
  'wakil_rektor_2',
  'wakil_rektor_3',
  'wakil_rektor_4',
];

const ARSIP_ALLOWED_ROLES = [
  'admin',
  'lpm',
  'rektor',
];

// ============ IDLE TIMEOUT MANAGER ============
// Komponen ini mengelola logout otomatis berdasarkan pengaturan session_lifetime.
const IdleTimeoutManager: React.FC = () => {
  const { isAuthenticated, logout } = useAuthStore();
  const [sessionLifetimeMs, setSessionLifetimeMs] = useState<number>(120 * 60 * 1000); // default 120 menit

  useEffect(() => {
    if (!isAuthenticated) return;

    // Ambil pengaturan session_lifetime dari backend
    api.get('/admin/settings/session_lifetime')
      .then((res) => {
        const value = res.data?.data?.value ?? res.data?.value;
        const menit = parseInt(value ?? '120', 10);
        if (!isNaN(menit) && menit > 0) {
          setSessionLifetimeMs(menit * 60 * 1000);
        }
      })
      .catch(() => {
        // Gunakan nilai default jika gagal mengambil dari backend
      });
  }, [isAuthenticated]);

  // Logout ke backend sekaligus reset state lokal
  const handleTimeout = React.useCallback(async () => {
    try {
      await api.post('/logout');
    } catch {
      // Abaikan error logout API, tetap clear state lokal
    }
    logout();
  }, [logout]);

  useIdleTimeout({
    timeoutMs: sessionLifetimeMs,
    onTimeout: handleTimeout,
    enabled: isAuthenticated,
  });

  return null;
};

// ============ APP ============

const App: React.FC = () => {
  return (
    <LoadingProvider>
    <BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: { borderRadius: '12px', fontSize: '14px' },
        }}
      />

      {/* Pengelola logout otomatis — tidak merender UI apapun */}
      <IdleTimeoutManager />

      <Routes>
        {/* ============ GUEST ROUTES ============ */}
        <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
        <Route path="/forgot-password" element={<GuestRoute><ForgotPassword /></GuestRoute>} />
        <Route path="/reset-password" element={<GuestRoute><ResetPassword /></GuestRoute>} />

        {/* ============ PROTECTED ROUTES ============ */}
        <Route element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>

          {/* Dashboard (per role) */}
          <Route path="/dashboard" element={<RoleBasedDashboard />} />
          <Route path="/dashboard/warek" element={<DashboardWarek />} />
          <Route path="/dashboard/dekan" element={<DashboardDekan />} />
          <Route path="/dashboard/kaprodi" element={<DashboardKaprodi />} />
          <Route path="/dashboard/lpm" element={<DashboardLPM />} />
          <Route path="/dashboard/admin" element={<DashboardAdmin />} />

          {/* Laporan Masalah */}
          <Route path="/laporan" element={<LaporanList />} />
          <Route
            path="/arsip"
            element={(
              <RoleRoute allowedRoles={ARSIP_ALLOWED_ROLES}>
                <Arsip />
              </RoleRoute>
            )}
          />
          <Route path="/laporan/buat" element={<LaporanCreate />} />
          <Route path="/laporan/:id" element={<LaporanDetail />} />
          <Route path="/laporan/:id/edit" element={<LaporanEdit />} />

          {/* Validasi */}
          <Route path="/validasi/level1" element={<ValidasiLevel1 />} />
          <Route path="/validasi/level2" element={<ValidasiLevel2 />} />

          {/* RAPIM */}
          <Route path="/rapim" element={<RapimCalendar />} />
          <Route path="/rapim/buat" element={<RapimCreate />} />
          <Route path="/rapim/:id" element={<RapimDetail />} />
          <Route path="/rapim/:id/keputusan" element={<RapimKeputusan />} />
          <Route path="/rapim/:id/presentasi" element={<RapimPresentasi />} />

          {/* Monitoring */}
          <Route
            path="/monitoring"
            element={
              <RoleRoute allowedRoles={MONITORING_ALLOWED_ROLES}>
                <MonitoringDashboard />
              </RoleRoute>
            }
          />
          <Route path="/tindak-lanjut/:id" element={<TindakLanjutDetail />} />

          {/* Reports & Analytics */}
          <Route path="/reports/builder" element={<ReportBuilder />} />

          {/* Admin */}
          <Route path="/admin/users" element={<UserManagement />} />
          <Route path="/admin/units" element={<UnitManagement />} />
          <Route path="/admin/master" element={<MasterData />} />
          <Route path="/admin/standar" element={<MasterData />} />
          <Route path="/admin/role-scopes" element={<RoleScopes />} />
          <Route path="/admin/settings" element={<SystemSettings />} />
          <Route path="/admin/logs" element={<AuditLogs />} />

          {/* Profile */}
          <Route path="/profile" element={<Profile />} />
          <Route path="/settings" element={<UserSettings />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/notifications" element={<NotificationsPage />} />

        </Route>

        {/* ============ REDIRECTS ============ */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
    </LoadingProvider>
  );
};

export default App;
